import React, { useState } from "react";
import styled from "styled-components/macro";
import numeral from "numeral";
import _ from "lodash";
import { ButtonText } from "@loanbossinc/component-library";
import { ReactComponent as TableRows } from "../../svgs/TableRows.svg";
import { authorizedRequest } from "utils/api";
import { ToastError } from "components/Global/Toasts/Toasty";

const SectionWrapper = styled.div`
  width: 100%;
  background: #ffffff;
  border: 1px solid #dadce0;
  box-sizing: border-box;
  box-shadow: 0px 10px 20px rgba(173, 174, 174, 0.25);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  font-family: "Work Sans";
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  margin-top: 2em;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #dadce0;
  padding: 0.7em 0 0.7em 1em;
`;

const StyledButtonText = styled(ButtonText)`
  .label {
    margin-right: 14px;
    font-family: "Work Sans";
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
  }
`;

const CalculationComponentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.7em 1em;
  border-top: ${({ withBorder }) => withBorder && "1px solid #dadce0"};
`;

const LeftSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const RightSectionWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const ValueWrapper = styled.div`
  font-weight: 500;
  font-size: 24px;
  line-height: 28px;
  height: 100%;
`;

const DownloadAllLink = styled.div`
  margin-left: auto;
  padding-right: 20px;
  font-family: Work Sans;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  color: ${({ isDownloadingAll }) => (isDownloadingAll ? "#9aa6b1" : "#005AA9")};
  display: flex;
  align-items: center;
  text-align: right;
  cursor: pointer;
`;

const ApprovedPreviewCalculationsSection = ({ taskDetails }) => {
  const [isDownloading, setIsDownloading] = useState({
    dscr: false,
    debtYield: false
  });
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const { reportComponentsConfig, reportGroupInstance, deal, emailAuth } = taskDetails;
  const rgi = reportGroupInstance.Items[0];
  const pinAuthCode = emailAuth?.authCode;

  const { shouldIncludeDscrInReport, shouldIncludeDebtYieldInReport } = rgi;

  const dscrAndDyComponentsFiltered = reportComponentsConfig.filter((component) => {
    if (component.type === "NOI") {
      return false;
    }

    // we need to check if dscr/dy are needed to be included in the report
    if (component.type === "DSCR") {
      return typeof shouldIncludeDscrInReport === "undefined" ? true : shouldIncludeDscrInReport;
    }

    if (component.type === "DebtYield") {
      return typeof shouldIncludeDebtYieldInReport === "undefined" ? true : shouldIncludeDebtYieldInReport;
    }

    return false;
  });

  const sortedDscrAndDyComponents = _.orderBy(dscrAndDyComponentsFiltered, ["type"], ["asc"]);

  if (!sortedDscrAndDyComponents.length) {
    return null;
  }

  const calculatedDscr = rgi.reportInstances && rgi.reportInstances.find((reportInstance) => reportInstance.type === "DSCR");
  const calculatedDy = rgi.reportInstances && rgi.reportInstances.find((reportInstance) => reportInstance.type === "DebtYield");

  const getCalculatedValue = (reportInstanceType) => {
    if (reportInstanceType === "DSCR" && calculatedDscr && calculatedDscr.total) {
      return `${numeral(calculatedDscr.total).format("0.00", Math.round)}x`;
    } else if (reportInstanceType === "DebtYield" && calculatedDy && calculatedDy.total) {
      return numeral(calculatedDy.total).format("0.00%", Math.round);
    } else {
      return "--";
    }
  };

  const getFormattedType = (type) => {
    if (type === "DSCR") {
      return "DSCR";
    }
    return "Debt Yield";
  };

  const downloadOneFile = async (type) => {
    const authCodeQuery = pinAuthCode ? `?authCode=${pinAuthCode}` : "";
    setIsDownloading((prev) => ({
      ...prev,
      [type]: true
    }));
    const response = await authorizedRequest(
      `/report-groups/instances/createExcelDocuments/${rgi.id}/${type}/${deal.id}/${encodeURIComponent(deal.name)}${authCodeQuery}`,
      "GET",
      null,
      null,
      true
    );

    if (response.url) {
      const link = document.createElement("a");
      link.href = response.url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      ToastError("Unable to generate the report.");
    }
    setIsDownloading((prev) => ({
      ...prev,
      [type]: false
    }));
  };

  const downloadAll = async () => {
    const authCodeQuery = pinAuthCode ? `?authCode=${pinAuthCode}` : "";
    setIsDownloadingAll(true);
    const linksPromises = [];
    sortedDscrAndDyComponents.forEach((component) => {
      linksPromises.push(
        authorizedRequest(`/report-groups/instances/createExcelDocuments/${rgi.id}/${component.type}/${deal.id}/${encodeURIComponent(deal.name)}${authCodeQuery}`, "GET", null, null, true)
      );
    });

    const links = await Promise.all(linksPromises);
    const nullLink = links.find((link) => !link.url);
    if (nullLink) {
      ToastError("Unable to generate the report.");
    }
    const filteredLinks = links.filter((link) => link.url);

    const delayInMs = 200;

    for (let i = 0; i < filteredLinks.length; i++) {
      setTimeout(
        (path) => {
          window.location = path;
        },
        delayInMs + i * delayInMs,
        filteredLinks[i].url
      );
    }
    setIsDownloadingAll(false);
  };

  return (
    <SectionWrapper>
      <HeaderRow>
        <div>Report Calculations</div>
        <DownloadAllLink onClick={!isDownloadingAll && downloadAll} isDownloadingAll={isDownloadingAll}>
          {isDownloadingAll ? "Downloading..." : "Download all"}
        </DownloadAllLink>
      </HeaderRow>
      {sortedDscrAndDyComponents.map((component, index) => {
        return (
          <CalculationComponentWrapper key={component.type} withBorder={index !== 0}>
            <LeftSectionWrapper>
              <div style={{ marginBottom: "12px" }}>{getFormattedType(component.type)}</div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <TableRows style={{ marginRight: "8px" }} />
                <StyledButtonText
                  label={isDownloading[component.type] ? "Downloading..." : "Download file"}
                  disabled={isDownloading[component.type]}
                  onClick={() => downloadOneFile(component.type)}
                />
              </div>
            </LeftSectionWrapper>
            <RightSectionWrapper>
              <ValueWrapper>{getCalculatedValue(component.type)}</ValueWrapper>
            </RightSectionWrapper>
          </CalculationComponentWrapper>
        );
      })}
    </SectionWrapper>
  );
};

export default ApprovedPreviewCalculationsSection;
